import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Pause, Play, X } from 'lucide-react';
import { gymSocialMutedText } from '../gymPageTheme';

const STATUS_STYLES = {
  scheduled: 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100',
  sending: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
  sent: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-white/70',
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800/80 dark:text-white/60',
};

export default function GymEmailCampaignHistory({
  campaigns,
  expandedId,
  detail,
  detailLoading,
  onToggleDetail,
  onCancel,
  onPause,
  onResume,
}) {
  const { t } = useTranslation();

  const statusLabel = (status) => {
    const key = `gym.email.campaigns.status.${status}`;
    const translated = t(key);
    return translated !== key ? translated : status;
  };

  const formatWhen = (raw) => {
    if (!raw) {
      return '';
    }
    return new Date(String(raw).replace(' ', 'T')).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const recurrenceLabel = (c) => {
    const rule = c.recurrence_rule;
    if (!rule?.type) {
      return '';
    }
    const interval = rule.interval || 1;
    if (rule.type === 'monthly') {
      return interval === 1
        ? t('gym.email.campaigns.recurrenceMonthly')
        : `${t('gym.email.campaigns.recurrenceInterval')} ${interval} ${t('gym.email.campaigns.recurrenceIntervalMonths')}`;
    }
    return interval === 1
      ? t('gym.email.campaigns.recurrenceWeekly')
      : `${t('gym.email.campaigns.recurrenceInterval')} ${interval} ${t('gym.email.campaigns.recurrenceIntervalWeeks')}`;
  };

  if (campaigns.length === 0) {
    return <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.campaigns.empty')}</p>;
  }

  return (
    <ul className="space-y-2 flex-1 min-h-0 overflow-y-auto max-h-[min(70vh,720px)] pr-1 -mr-1">
      {campaigns.map((c) => {
        const expanded = expandedId === c.id;
        const badgeClass = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
        const isRecurring = !!c.recurrence_enabled;
        const isPaused = isRecurring && !!c.recurrence_paused;
        const whenRaw = c.next_run_at || c.scheduled_at || c.sent_at || c.created_at;

        return (
          <li
            key={c.id}
            className="rounded-lg border border-gray-200/90 bg-white/60 dark:border-white/10 dark:bg-gray-800/40 overflow-hidden"
          >
            <div className="flex items-stretch">
              <button
                type="button"
                onClick={() => onToggleDetail(c.id)}
                className="flex-1 flex items-start gap-3 px-4 py-3.5 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/25 transition-colors"
              >
                <Mail className="h-4 w-4 shrink-0 mt-0.5 opacity-50" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm leading-snug text-gray-900 dark:text-white line-clamp-2">
                    {c.subject}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}>
                      {statusLabel(c.status)}
                    </span>
                    {isRecurring && (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100">
                        {t('gym.email.campaigns.recurrenceBadge')}
                      </span>
                    )}
                    {isPaused && (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100">
                        {t('gym.email.campaigns.recurrencePaused')}
                      </span>
                    )}
                    {c.status === 'scheduled' && whenRaw && (
                      <span className={`text-[11px] ${gymSocialMutedText}`}>
                        {isRecurring
                          ? t('gym.email.campaigns.recurrenceNextRun', { when: formatWhen(whenRaw) })
                          : formatWhen(whenRaw)}
                      </span>
                    )}
                    {isRecurring && (c.recurrence_occurrence_count > 0 || (detail?.campaign?.id === c.id && detail?.runs?.length)) && (
                      <span className={`text-[11px] ${gymSocialMutedText}`}>
                        {t('gym.email.campaigns.recurrenceOccurrences', {
                          count: c.recurrence_occurrence_count || detail?.runs?.length || 0,
                        })}
                      </span>
                    )}
                    {(c.status === 'sent' || c.status === 'failed') && !isRecurring && (
                      <span className={`text-[11px] ${gymSocialMutedText}`}>
                        {c.success_count}/{c.recipient_count}
                      </span>
                    )}
                    {isRecurring && recurrenceLabel(c) && (
                      <span className={`text-[11px] ${gymSocialMutedText}`}>{recurrenceLabel(c)}</span>
                    )}
                  </div>
                </div>
              </button>
              {c.status === 'scheduled' && isRecurring && !isPaused && onPause && (
                <button
                  type="button"
                  title={t('gym.email.campaigns.pauseRecurrence')}
                  onClick={() => onPause(c.id)}
                  className="px-3 border-l border-gray-200 dark:border-white/10 text-gray-500 hover:bg-amber-50 dark:hover:bg-amber-900/25 transition-colors"
                >
                  <Pause className="h-4 w-4" />
                </button>
              )}
              {c.status === 'scheduled' && isRecurring && isPaused && onResume && (
                <button
                  type="button"
                  title={t('gym.email.campaigns.resumeRecurrence')}
                  onClick={() => onResume(c.id)}
                  className="px-3 border-l border-gray-200 dark:border-white/10 text-gray-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/25 transition-colors"
                >
                  <Play className="h-4 w-4" />
                </button>
              )}
              {c.status === 'scheduled' && (
                <button
                  type="button"
                  title={t('gym.email.campaigns.cancelSchedule')}
                  onClick={() => onCancel(c.id)}
                  className="px-3 border-l border-gray-200 dark:border-white/10 text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/25 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {expanded && (
              <div className="border-t border-gray-200 dark:border-white/10 px-4 py-3 text-sm bg-gray-50/50 dark:bg-gray-800/40">
                {detailLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {!detailLoading && detail?.runs?.length > 0 && (
                  <div className="mb-3">
                    <p className={`text-xs font-medium mb-1 ${gymSocialMutedText}`}>
                      {t('gym.email.campaigns.recurrenceRunsTitle')}
                    </p>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {detail.runs.map((run) => (
                        <li key={run.id} className={`text-xs ${gymSocialMutedText}`}>
                          {t('gym.email.campaigns.recurrenceRunLine', {
                            n: run.occurrence_number,
                            when: formatWhen(run.sent_at),
                            success: run.success_count,
                            total: run.recipient_count,
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!detailLoading && detail?.recipients?.length > 0 && (
                  <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {detail.recipients.map((r) => (
                      <li key={r.user_id} className={gymSocialMutedText}>
                        {r.name || r.email} - {r.status}
                        {r.error_message ? `: ${r.error_message}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
                {!detailLoading && detail?.recipients?.length === 0 && c.status === 'scheduled' && (
                  <p className={gymSocialMutedText}>{t('gym.email.campaigns.scheduledPending')}</p>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
